import { useQuery } from '@apollo/client';
import { Button, Divider, List, ListItem, ListItemText, useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Add } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AgentConversation, GET_AGENT_CONVERSATIONS } from '../../graphql/queries/agent';
import { useAgent } from '../../hooks/use-agent';
import { useSession } from '../../hooks/use-session';
import { sidebarStyles } from './agent-page.styles';

const SKELETON_HEIGHT_PRIMARY = 24;
const SKELETON_HEIGHT_SECONDARY = 16;

interface ConversationHistorySidebarProps {
    variant?: 'page' | 'sidebar';
}

export const ConversationHistorySidebar: React.FC<ConversationHistorySidebarProps> = ({ variant = 'page' }) => {
    const theme = useTheme<Theme>();
    const history = useHistory();
    const location = useLocation();
    const session = useSession();
    const userId = session?.user?.id;
    const { conversationId, onSelectConversation, onNewChat } = useAgent();
    const isOnAgentPage = location.pathname.startsWith('/orbit');

    const { data, loading, refetch } = useQuery<{ agent_conversations: AgentConversation[] }>(GET_AGENT_CONVERSATIONS, {
        pollInterval: 60000, // Poll every 1 minute to keep list fresh
        skip: !userId,
        variables: { userId }
    });

    // Re-fetch when conversation ID changes (new conversation created)
    React.useEffect(() => {
        if (conversationId) {
            refetch();
        }
    }, [conversationId, refetch]);

    const conversations = data?.agent_conversations || [];

    const handleConversationClick = React.useCallback(
        (id: string) => {
            onSelectConversation(id);
            // Only navigate when on the agent page variant (not in sidebar mode)
            if (variant === 'page' && isOnAgentPage) {
                history.push(`/orbit/chat/${id}`);
            }
        },
        [onSelectConversation, history, isOnAgentPage, variant]
    );

    const handleNewChat = React.useCallback(() => {
        onNewChat();
        // Only navigate when on the agent page variant (not in sidebar mode)
        if (variant === 'page' && isOnAgentPage) {
            history.push('/orbit');
        }
    }, [onNewChat, history, isOnAgentPage, variant]);

    const conversationItems = conversations.map((conversation) => (
        <ListItem
            button={true}
            key={conversation.id}
            selected={conversationId === conversation.id}
            onClick={handleConversationClick.bind(null, conversation.id)}
            className="sidebar__listItem"
        >
            <ListItemText
                primary={conversation.title || 'New chat'}
                secondary={new Date(conversation.lastMessageAt).toLocaleDateString()}
                className="sidebar__itemText"
            />
        </ListItem>
    ));

    const emptyState =
        conversations.length === 0 && !loading ? <div className="sidebar__empty">No previous conversations</div> : null;

    const skeletonItems = Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="sidebar__skeletonItem">
            <Skeleton variant="text" width="80%" height={SKELETON_HEIGHT_PRIMARY} />
            <Skeleton variant="text" width="40%" height={SKELETON_HEIGHT_SECONDARY} />
        </div>
    ));

    const loadingSkeletons =
        loading && conversations.length === 0 ? <div className="sidebar__loading">{skeletonItems}</div> : null;

    const conversationList = loadingSkeletons || (
        <List className="sidebar__list">
            {conversationItems}
            {emptyState}
        </List>
    );

    const newChatButton =
        variant === 'page' ? (
            <>
                <div className="sidebar__header">
                    <Button className="sidebar__newChatBtn" onClick={handleNewChat}>
                        <Add fontSize="small" />
                        New chat
                    </Button>
                </div>
                <Divider />
            </>
        ) : null;

    return (
        <div css={sidebarStyles(theme)} className="conversation-history-sidebar">
            {newChatButton}
            {conversationList}
        </div>
    );
};
